import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, UploadCloud } from 'lucide-react';
import { clsx } from 'clsx';
import { optimizeImage, isAcceptableImage } from '../../lib/optimize-image';
import { uploadImageToR2, type R2Folder } from '../../lib/upload-r2';
import { reportError } from '../../lib/report-error';

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  bucket: R2Folder;
  folder?: string;
  label?: string;
  title?: string;
  description?: string;
  variant?: 'avatar' | 'cover' | 'logo' | 'hero';
  /** Called with the original file before optimization/upload (e.g. to sample its bg color). */
  onFileSelected?: (file: File) => void;
  theme?: 'light' | 'dark' | 'high-contrast';
  disabled?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value, onChange, bucket, folder = '', label, title: uploadTitle, description: uploadDescription, variant = 'cover', theme = 'light', disabled = false, onFileSelected,
}) => {
  const isLight = theme !== 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    if (!isAcceptableImage(file)) return;
    onFileSelected?.(file);
    setIsUploading(true);

    try {
      // Per-variant optimization budget. A hero is a full-bleed background on a
      // client's public website, so it gets a much larger dimension/byte budget
      // and is re-encoded to WebP — this keeps a detailed photo crisp at 2560px
      // instead of being downscaled to fit a tight 1.5MB PNG cap (the bug that
      // left early heroes pixelated at ~1562px). Logos keep their source format
      // for transparency.
      // Avatars were capped at 512px back when they only rendered as a ~112px
      // CRM circle. The agency-site /agentes page now shows them as a ~355 CSS px
      // aspect-square card — 1065 device px at DPR 3 — and Cloudflare never
      // upscales, so a 512px source was being blown up 2-3x and looked soft.
      const optimizeOpts =
        variant === 'avatar'
          ? { maxDimension: 1024, maxSizeMB: 1 }
          : variant === 'hero'
            ? { maxDimension: 2560, maxSizeMB: 4, fileType: 'image/webp' as const }
            : { maxDimension: 1920, maxSizeMB: 1.5 };
      const compressed = await optimizeImage(file, optimizeOpts);

      const { url } = await uploadImageToR2(compressed, {
        folder: bucket,
        subfolder: folder || undefined,
      });

      onChange(url);
    } catch (err: any) {
      reportError(err, { feature: 'image_upload', variant, bucket });
      alert(`Error al subir imagen: ${err.message || 'Intenta de nuevo'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || isUploading) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleRemove = () => {
    onChange('');
  };

  const avatarSize = 'w-28 h-28';
  const coverSize = 'w-full h-36';
  const logoSize = 'w-28 h-28';

  const sizeClass = variant === 'avatar' ? avatarSize : variant === 'logo' ? logoSize : coverSize;
  const roundedClass = variant === 'avatar' ? 'rounded-3xl' : variant === 'logo' ? 'rounded-2xl' : 'rounded-xl';

  return (
    <div className="space-y-2">
      {label && <label className="text-[10px] uppercase font-black tracking-widest text-slate-400">{label}</label>}

      {value ? (
        // Preview with remove/change
        <div className={clsx("relative group", sizeClass, roundedClass, "overflow-hidden border", isLight ? "border-slate-200" : "border-white/10")}>
          <img
            src={value}
            alt={label || 'Imagen'}
            className={clsx("w-full h-full", variant === 'logo' ? 'object-contain p-2' : 'object-cover')}
          />
          {!disabled && (
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-all"
                title="Cambiar imagen"
              >
                <Upload size={16} />
              </button>
              <button
                onClick={handleRemove}
                className="p-2 rounded-xl bg-white/20 text-white hover:bg-rose-500/80 transition-all"
                title="Eliminar"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      ) : (
        // Upload zone
        <div
          onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={clsx(
            "flex flex-col items-center justify-center border-2 border-dashed transition-all group",
            uploadTitle ? "gap-3 p-8 rounded-2xl" : clsx("gap-2", sizeClass, roundedClass),
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            dragOver
              ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10"
              : isLight
                ? "border-slate-300 hover:border-indigo-400 bg-slate-50 hover:bg-slate-100"
                : "border-white/20 hover:border-indigo-500/50 bg-black/10 hover:bg-white/5"
          )}
        >
          {isUploading ? (
            <Loader2 size={28} className="text-indigo-500 animate-spin" />
          ) : uploadTitle ? (
            <>
              <div className={clsx("w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110", isLight ? "bg-white shadow-sm text-indigo-500" : "bg-white/10 text-indigo-400")}>
                <UploadCloud size={28} />
              </div>
              <p className={clsx("font-bold text-lg", isLight ? "text-slate-800" : "text-white")}>
                {dragOver ? 'Suelta la imagen aquí' : uploadTitle}
              </p>
              {uploadDescription && (
                <p className="text-sm text-slate-500 text-center max-w-sm">{uploadDescription}</p>
              )}
            </>
          ) : (
            <>
              <ImageIcon size={22} className="text-slate-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {dragOver ? 'Soltar aquí' : 'Subir imagen'}
              </span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif,.dng,.tif,.tiff"
        className="hidden"
        disabled={disabled || isUploading}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
};
